/**
 * Delivery status — what every client has, and what failed.
 *
 * The other delivery screens are about configuration: who the clients are, what
 * they bought, where their files go. This one is about the running system, and
 * it answers the two questions that actually come up:
 *
 *   "Did NDTV get the medal tally, and when?"     -> the table
 *   "This FTP was down, send it again"            -> Send now / Try again
 *
 * Nothing is sent from this screen. It marks a file as owed again, and the
 * delivery worker picks it up within a few seconds — one sender, one set of
 * retries, one record of what happened.
 */

import { useCallback, useState } from "react";

import {
  Button,
  Card,
  Empty,
  PageHead,
  Pill,
  Problem,
  Section,
  Table,
  Td,
  Th,
} from "@/components/kit";
import {
  listDeadLetters,
  listDeliveryStatus,
  replayDeadLetter,
  resolveDeadLetter,
  sendAssignmentNow,
} from "@/lib/api/client";
import type { DeliveryDeadLetter, DeliveryStatusRow } from "@/lib/api/types";
import { errorText, useApi } from "@/spa/useApi";

/** "2 min ago" — how long since something happened, in words. */
function ago(at?: string | null): string {
  if (!at) return "never";
  const seconds = Math.max(0, (Date.now() - new Date(at).getTime()) / 1000);
  if (seconds < 60) return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  return `${Math.round(seconds / 86400)} d ago`;
}

function size(bytes?: number | null): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Every timing a client can be given, said the way a person would say it. */
function cadence(row: DeliveryStatusRow): string {
  const parts: string[] = [];
  if (row.scheduleSeconds > 0) parts.push(`every ${row.scheduleSeconds}s`);
  if (row.throttleSeconds > 0)
    parts.push(`at most every ${row.throttleSeconds}s`);
  return parts.length ? parts.join(", ") : "on every change";
}

function StatusPill({ row }: { row: DeliveryStatusRow }) {
  if (!row.isActive) return <Pill tone="neutral">off</Pill>;
  if (row.status === "failed")
    return (
      <Pill tone="bad" dot>
        failing
      </Pill>
    );
  if (row.status === "skipped")
    return <Pill tone="neutral">not sent here</Pill>;
  if (row.behind)
    return (
      <Pill tone="warn" dot>
        behind
      </Pill>
    );
  if (row.status === "ok")
    return (
      <Pill tone="good" dot>
        delivered
      </Pill>
    );
  return <Pill tone="neutral">nothing sent yet</Pill>;
}

function StatusTable({
  rows,
  onChanged,
}: {
  rows: DeliveryStatusRow[];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  async function sendNow(row: DeliveryStatusRow) {
    setBusy(row.assignmentId);
    setError(undefined);
    try {
      await sendAssignmentNow(row.assignmentId);
      onChanged();
    } catch (err) {
      setError(errorText(err, "could not do that"));
    } finally {
      setBusy(undefined);
    }
  }

  if (!rows.length)
    return (
      <Empty
        title="No deliveries yet"
        note="A client with a contract gets its files on the next pass."
      />
    );
  return (
    <>
      {error ? <Problem>{error}</Problem> : null}
      <Table>
        <thead>
          <tr>
            <Th>Client</Th>
            <Th>Feed</Th>
            <Th>Where</Th>
            <Th>When</Th>
            <Th>State</Th>
            <Th>Last delivery</Th>
            <Th>Sent</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.assignmentId}:${row.docKey ?? "none"}`}>
              <Td>{row.clientCode}</Td>
              <Td>
                {row.feedKey}
                {row.docKey ? (
                  <div className="text-xs opacity-60">{row.docKey}</div>
                ) : null}
              </Td>
              <Td>
                {row.deliveryType}
                <div className="text-xs opacity-60">{row.destination}</div>
              </Td>
              <Td>{cadence(row)}</Td>
              <Td>
                <StatusPill row={row} />
                {row.lastError ? (
                  <div className="text-xs opacity-70">
                    {row.lastError.slice(0, 120)}
                  </div>
                ) : null}
              </Td>
              <Td>
                {ago(row.lastDeliveredAt)}
                <div className="text-xs opacity-60">
                  {size(row.lastBytes)}
                  {row.lastDurationMs ? ` in ${row.lastDurationMs} ms` : ""}
                </div>
              </Td>
              <Td>
                {row.deliveries}
                {row.failures ? (
                  <span className="text-xs opacity-70">
                    {" "}
                    / {row.failures} failed
                  </span>
                ) : null}
              </Td>
              <Td>
                <Button
                  onClick={() => sendNow(row)}
                  disabled={busy === row.assignmentId || !row.isActive}
                >
                  {busy === row.assignmentId ? "Queueing…" : "Send now"}
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

function DeadLetters({
  rows,
  onChanged,
}: {
  rows: DeliveryDeadLetter[];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  async function act(id: string, what: "replay" | "resolve") {
    setBusy(id);
    setError(undefined);
    try {
      await (what === "replay" ? replayDeadLetter(id) : resolveDeadLetter(id));
      onChanged();
    } catch (err) {
      setError(errorText(err, "could not do that"));
    } finally {
      setBusy(undefined);
    }
  }

  if (!rows.length)
    return (
      <Empty
        title="Nothing has given up"
        note="A delivery that runs out of tries lands here."
      />
    );
  return (
    <>
      {error ? <Problem>{error}</Problem> : null}
      <Table>
        <thead>
          <tr>
            <Th>Feed</Th>
            <Th>Where</Th>
            <Th>Tries</Th>
            <Th>What went wrong</Th>
            <Th>When</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <Td>
                {row.feedKey}
                <div className="text-xs opacity-60">{row.docKey}</div>
              </Td>
              <Td>
                {row.deliveryType}
                <div className="text-xs opacity-60">{row.destination}</div>
              </Td>
              <Td>{row.attempts}</Td>
              <Td>
                <div className="text-xs">{row.lastError.slice(0, 160)}</div>
              </Td>
              <Td>{ago(row.updatedAt)}</Td>
              <Td>
                <div className="flex gap-2">
                  <Button
                    onClick={() => act(row.id, "replay")}
                    disabled={busy === row.id}
                  >
                    Try again
                  </Button>
                  <Button
                    onClick={() => act(row.id, "resolve")}
                    disabled={busy === row.id}
                  >
                    Mark handled
                  </Button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

export function DeliveryStatusPage() {
  const [failingOnly, setFailingOnly] = useState(false);
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const status = useApi(
    useCallback(
      () => listDeliveryStatus({ failing: failingOnly }),
      [failingOnly],
    ),
    [failingOnly, nonce],
    "could not load delivery status",
  );
  const dead = useApi(
    useCallback(() => listDeadLetters(false), []),
    [nonce],
    "could not load failures",
  );

  const rows = status.data ?? [];
  const failing = rows.filter((r) => r.status === "failed" || r.behind).length;

  return (
    <div className="space-y-6">
      <PageHead
        title="Delivery status"
        note="What every client has, what failed, and how to send it again."
      />
      <Card>
        <Section
          title={`Files (${rows.length})`}
          actions={
            <div className="flex gap-2">
              <Button onClick={() => setFailingOnly((v) => !v)}>
                {failingOnly
                  ? "Show all"
                  : `Only problems${failing ? ` (${failing})` : ""}`}
              </Button>
              <Button onClick={refresh}>Refresh</Button>
            </div>
          }
        >
          {status.error ? (
            <Problem>
              {errorText(status.error, "could not load status")}
            </Problem>
          ) : null}
          {status.loading ? (
            <Empty title="Loading…" />
          ) : (
            <StatusTable rows={rows} onChanged={refresh} />
          )}
        </Section>
      </Card>
      <Card>
        <Section title={`Gave up (${dead.data?.length ?? 0})`}>
          {dead.error ? (
            <Problem>
              {errorText(dead.error, "could not load failures")}
            </Problem>
          ) : null}
          {dead.loading ? (
            <Empty title="Loading…" />
          ) : (
            <DeadLetters rows={dead.data ?? []} onChanged={refresh} />
          )}
        </Section>
      </Card>
    </div>
  );
}
